import type { ContactMessage, ContactSection } from '@/types';

import React from 'react';
import { useAtomValue } from 'jotai';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Select,
  SelectItem,
  Spinner,
  Textarea
} from '@heroui/react';
import { toast } from 'react-toastify';
import { RiSendPlaneLine, RiListCheck, RiQuestionAnswerLine } from '@remixicon/react';

import EmptyLayout from '@/layouts/empty';
import { profileAtom } from '@/store';
import { publicApiClient } from '@/utils/publicApi';
import { ErrorHandler } from '@/utils/errorHandler';

interface ContactFormState {
  section_id: string;
  subject: string;
  body: string;
  name: string;
  email: string;
}

const initialFormState: ContactFormState = {
  section_id: '',
  subject: '',
  body: '',
  name: '',
  email: ''
};

export default function ContactPage() {
  const profile = useAtomValue(profileAtom);

  const [sections, setSections] = React.useState<ContactSection[]>([]);
  const [isLoadingSections, setIsLoadingSections] = React.useState(true);
  const [form, setForm] = React.useState<ContactFormState>(initialFormState);
  const [formErrors, setFormErrors] = React.useState<Partial<Record<keyof ContactFormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [createdMessage, setCreatedMessage] = React.useState<ContactMessage | null>(null);

  const [replyMessageId, setReplyMessageId] = React.useState('');
  const [replyBody, setReplyBody] = React.useState('');
  const [isReplying, setIsReplying] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    setIsLoadingSections(true);
    publicApiClient
      .get('/contact-sections')
      .then(({ data }) => {
        if (!mounted) return;
        const records: ContactSection[] = data?.data ?? data ?? [];
        setSections(records);
        if (records.length > 0) {
          setForm((prev) => ({
            ...prev,
            section_id: prev.section_id || String(records[0].id)
          }));
        }
      })
      .catch(() => {
        if (!mounted) return;
        setSections([]);
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoadingSections(false);
      });

    return () => {
      mounted = false;
    };
  }, [setForm]);

  const resetFormErrors = React.useCallback(() => {
    setFormErrors({});
  }, []);

  const handleSubmit = React.useCallback(async () => {
    resetFormErrors();

    if (!form.section_id || !form.body.trim()) {
      setFormErrors((prev) => ({
        ...prev,
        section_id: !form.section_id ? 'Please choose a topic' : prev.section_id,
        body: !form.body.trim() ? 'Message is required' : prev.body
      }));
      return;
    }

    if (!profile && (!form.name.trim() || !form.email.trim())) {
      setFormErrors((prev) => ({
        ...prev,
        name: !form.name.trim() ? 'Name is required' : prev.name,
        email: !form.email.trim() ? 'Email is required' : prev.email
      }));
      return;
    }

    const payload: Record<string, unknown> = {
      section_id: Number(form.section_id),
      body: form.body.trim()
    };
    if (form.subject.trim()) {
      payload.subject = form.subject.trim();
    }
    if (!profile) {
      payload.name = form.name.trim();
      payload.email = form.email.trim();
    }

    try {
      setIsSubmitting(true);
      const { data } = await publicApiClient.post('/contact-messages', payload);
      const message: ContactMessage = data?.data ?? data;
      setCreatedMessage(message);
      toast.success('Message sent successfully!');
      setForm((prev) => ({
        ...prev,
        subject: '',
        body: ''
      }));
    } catch (error) {
      setFormErrors({
        section_id: ErrorHandler.getFieldError('section_id', error as any),
        subject: ErrorHandler.getFieldError('subject', error as any),
        name: ErrorHandler.getFieldError('name', error as any),
        email: ErrorHandler.getFieldError('email', error as any),
        body: ErrorHandler.getFieldError('body', error as any)
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [form, profile, resetFormErrors]);

  const handleReply = React.useCallback(async () => {
    if (!replyMessageId.trim() || !replyBody.trim()) {
      toast.warning('Message ID and reply body are required');
      return;
    }

    try {
      setIsReplying(true);
      await publicApiClient.post(`/contact-messages/${replyMessageId.trim()}`, {
        body: replyBody.trim()
      });
      toast.success('Reply sent!');
      setReplyBody('');
    } catch (error) {
      // ErrorHandler already notified via interceptor
    } finally {
      setIsReplying(false);
    }
  }, [replyBody, replyMessageId]);

  const nameDisabled = Boolean(profile);

  return (
    <EmptyLayout>
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 py-8">
        <div className="text-center">
          <p className="text-primary mb-2 text-sm font-semibold uppercase tracking-wide">Contact us</p>
          <h1 className="text-3xl font-bold">We’re here to help</h1>
          <p className="text-default-500 mt-2 text-base">
            Submit a request and our team will get back to you. If you already have an open conversation, you can reply with
            additional details below.
          </p>
        </div>

        <Card>
          <CardHeader className="flex items-center gap-3">
            <RiSendPlaneLine className="text-primary h-5 w-5" />
            <div>
              <h2 className="text-xl font-semibold">Start a new request</h2>
              <p className="text-default-500 text-sm">Tell us more about what you need help with.</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {isLoadingSections ? (
              <div className="flex items-center justify-center py-6">
                <Spinner label="Loading topics..." />
              </div>
            ) : (
              <>
                <Select
                  label="Topic"
                  selectedKeys={form.section_id ? new Set([form.section_id]) : new Set([])}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys as Set<string>)[0] || '';
                    setForm((prev) => ({ ...prev, section_id: value }));
                    setFormErrors((prev) => ({ ...prev, section_id: undefined }));
                  }}
                  isInvalid={Boolean(formErrors.section_id)}
                  errorMessage={formErrors.section_id}
                  isDisabled={sections.length === 0}
                >
                  {sections.map((section) => (
                    <SelectItem key={String(section.id)} description={section.description ?? undefined}>
                      {section.name}
                    </SelectItem>
                  ))}
                </Select>

                {!nameDisabled && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="Your name"
                      placeholder="Jane Doe"
                      value={form.name}
                      onValueChange={(value) => {
                        setForm((prev) => ({ ...prev, name: value }));
                        setFormErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      isInvalid={Boolean(formErrors.name)}
                      errorMessage={formErrors.name}
                    />
                    <Input
                      type="email"
                      label="Email"
                      placeholder="you@example.com"
                      value={form.email}
                      onValueChange={(value) => {
                        setForm((prev) => ({ ...prev, email: value }));
                        setFormErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      isInvalid={Boolean(formErrors.email)}
                      errorMessage={formErrors.email}
                    />
                  </div>
                )}

                <Input
                  label="Subject"
                  placeholder="Short summary (optional)"
                  value={form.subject}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, subject: value }));
                    setFormErrors((prev) => ({ ...prev, subject: undefined }));
                  }}
                  isInvalid={Boolean(formErrors.subject)}
                  errorMessage={formErrors.subject}
                />

                <Textarea
                  label="Message"
                  placeholder="Describe your issue or question..."
                  minRows={5}
                  value={form.body}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, body: value }));
                    setFormErrors((prev) => ({ ...prev, body: undefined }));
                  }}
                  isInvalid={Boolean(formErrors.body)}
                  errorMessage={formErrors.body}
                />

                <div className="flex justify-end">
                  <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit}>
                    Send message
                  </Button>
                </div>

                {createdMessage && (
                  <div className="rounded-large border border-success-200 bg-success-50 p-4 text-sm">
                    <p className="font-semibold text-success-700">Thanks! We received your request.</p>
                    <p className="text-default-600">
                      Ticket ID: <span className="font-mono">{createdMessage.id}</span>. Keep this ID handy when referencing your
                      request.
                    </p>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {profile && (
          <Card>
            <CardHeader className="flex items-center gap-3">
              <RiQuestionAnswerLine className="text-secondary h-5 w-5" />
              <div>
                <h2 className="text-xl font-semibold">Reply to an existing ticket</h2>
                <p className="text-default-500 text-sm">Use your ticket ID to send us an update.</p>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <Input
                label="Ticket ID"
                placeholder="Enter your ticket number"
                value={replyMessageId}
                onValueChange={setReplyMessageId}
              />
              <Textarea
                label="Reply message"
                minRows={4}
                placeholder="Add more context or an update"
                value={replyBody}
                onValueChange={setReplyBody}
              />
              <div className="flex justify-end">
                <Button color="secondary" isLoading={isReplying} onPress={handleReply}>
                  Send reply
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {sections.length > 0 && (
          <Card>
            <CardHeader className="flex items-center gap-3">
              <RiListCheck className="text-default-500 h-5 w-5" />
              <div>
                <h2 className="text-xl font-semibold">Available topics</h2>
                <p className="text-default-500 text-sm">Choose the one that best matches your request.</p>
              </div>
            </CardHeader>
            <CardBody className="grid gap-4 md:grid-cols-2">
              {sections.map((section) => (
                <div key={section.id} className="rounded-xl border border-default-200 p-4">
                  <p className="font-semibold">{section.name}</p>
                  <p className="text-default-500 text-sm">{section.description || 'General inquiries'}</p>
                </div>
              ))}
            </CardBody>
          </Card>
        )}
      </div>
    </EmptyLayout>
  );
}

